import { useMemo } from 'react';
import { s } from '@/theme/tokens';
import { CodeOutputBlock, Stack } from '@/ui';
import { useTheme } from '@/ui/primitives';
import { measureOutputPanelWidthPx } from './resultsFileLayout';
import type { DetailItem } from './resultsFileDetailUtils';
import type { SpacingToken } from '@/theme/tokens';
import type { ReactElement } from 'react';

export type ResultsFileOutputPanelProps = {
  items: DetailItem[];
  onCopy: (title: string, items: DetailItem[]) => void;
  itemGap?: SpacingToken;
  sharedWidthPx?: number;
};

export const ResultsFileOutputPanel = ({
  items,
  onCopy,
  itemGap = 'xs',
  sharedWidthPx,
}: ResultsFileOutputPanelProps): ReactElement | null => {
  const theme = useTheme();
  if (items.length === 0) {
    return null;
  }
  const measuredPanelWidthPx = useMemo(() => {
    return measureOutputPanelWidthPx({ items, theme });
  }, [items, theme]);
  const panelWidthPx = sharedWidthPx ?? measuredPanelWidthPx;
  return (
    <Stack gap={itemGap} sx={{ alignItems: 'flex-start' }}>
      {items.map((item) => (
        <CodeOutputBlock
          key={item.label}
          label={item.label}
          value={item.value}
          copyAriaLabel={`Copy ${item.label}`}
          sx={{
            width: `min(100%, ${panelWidthPx}px)`,
            minWidth: 0,
            maxWidth: '100%',
            marginRight: s('none'),
            alignSelf: 'flex-start',
            '& p': {
              overflowWrap: 'anywhere',
            },
          }}
          onCopy={() => {
            onCopy(item.label, [item]);
          }}
        />
      ))}
    </Stack>
  );
};
